import moment from "moment";
import React, { useState, useEffect, useRef } from "react";
import { connect, useSelector, useDispatch } from "react-redux";
import { createStructuredSelector } from "reselect";
import { withTranslation } from "react-i18next";
import { Form, Button, Space, Typography, Progress, Row, Col, Table } from "antd";
import styled from "styled-components";

import ACTIONS from "../store/action";
import { ApiNames } from "../api/api";
import { isOverdue } from "../utils/cycles";
import IsOverdueIcon from "../assets/svg/IsOverdueIcon";

import { NumericalInput } from "./form/NumericalInput";

import { OrderDateFormItem } from "./form/OrderDateFormItem";
import { AdminDateFormItem } from "./form/AdminDateFormItem";
import {
  selectIsFetching,
  selectPatientNotes,
  selectPatientCycles,
  selectSelectedOrderId,
  selectSelectedOrder
} from "../store/selector";
import { EstimatedDateFormItem } from "./form/EstimatedDateFormItem";
import OrderSwitchingDropdown from "./OrderSwitchingDropdown";
import { OrderTypeFormItem } from "./form/OrderTypeFormItem";
import { OrderType } from "../constant/orderType";
import { EditButton } from "./EditButton";
import { PatientOrderProgress } from "./PatientOrderProgress";
import { ArrivedDateFormItem } from "./form/ArrivedDateFormItem";
import { SEC_FONT_GREY } from "../constant/colors";
import { DrugPriceCalculator } from "./DrugPriceCalculator/DrugPriceCalculator";
import { SingleCycleForm } from "./SingleCycleForm";
import { LOT_NUMBER_MAX } from "../constant/cycles";
import { LotNumberFormItem } from "./form/LotNumberFormItem";
import { BillVoidedDateFormItem } from "./form/BillVoidedDateFormItem";
import { sizes } from "../constant/styles";

const { Text } = Typography;

const StyledRow = styled(Row)`
  .ant-row {
    display: block;
  }
`;

const PatientOrderInfoComponent = ({ t, patient, cycles, isFetching, setEdited, bulkChangeAct, patientNotes }) => {
  const dispatch = useDispatch();
  const selectedOrderId = useSelector(selectSelectedOrderId);
  const selectedOrder = useSelector(selectSelectedOrder);
  const [editable, setEditable] = useState(false);
  const [currCycleNotes, setCurrCycleNotes] = useState([]);
  const [form] = Form.useForm();
  const initialValuesRef = useRef(null);

  const displayOrder = selectedOrder || patient.currentOrder;

  useEffect(() => {
    if (!isFetching) {
      setEditable(false);
    }
  }, [isFetching]);

  useEffect(() => {
    const { currentOrder } = patient;
    if (currentOrder && currentOrder.id) {
      // If no order is selected, or if selected order doesn't exist in cycles, set to current order
      if (selectedOrderId === null || !cycles || !cycles.find((cycle) => cycle.id === selectedOrderId)) {
        dispatch(ACTIONS.setSelectedOrder(currentOrder.id));
      }
    }
  }, [patient, cycles, selectedOrderId, dispatch]);

  useEffect(() => {
    const orderIndex = displayOrder.orderIndex;

    const currCycleNotes = patientNotes
      .filter((patientNote) => patientNote.cycle === orderIndex + 1)
      .filter((patientNote) => !patientNote.isDeleted);

    setCurrCycleNotes(currCycleNotes);
  }, [patientNotes, displayOrder]);

  useEffect(() => {
    const { assignees } = patient;
    const {
      administrationDate,
      orderDate,
      billVoided,
      arriveDate,
      estimatedDelivery,
      isFreeDrug,
      unitCost,
      quantity,
      totalAmount,
      lotNumberDispensed,
      lotNumberReceived,
      accountNumber,
      billVoidedDate
    } = displayOrder;

    form.setFieldsValue({
      isFreeDrug,
      orderDate: orderDate ? moment(orderDate) : null,
      administrationDate: administrationDate ? moment(administrationDate) : null,
      arriveDate: arriveDate ? moment(arriveDate) : null,
      estimatedDelivery: estimatedDelivery ? moment(estimatedDelivery) : null,
      billVoided,
      assignees: assignees.map((assignee) => assignee.id),
      unitCost,
      quantity,
      lotNumberDispensed,
      lotNumberReceived,
      accountNumber,
      billVoidedDate: billVoidedDate ? moment(billVoidedDate) : null,
      totalAmount
    });
  }, [displayOrder, patient, form]);

  const onSubmit = (values) => {
    setEdited(false);
    initialValuesRef.current = null;
    const pharmacyPatient = {
      id: displayOrder.pharmacyPatient,
      pharmacyPatient: displayOrder.pharmacyPatient,
      administrationDate: values.administrationDate.toISOString(),
      orderDate: values.orderDate.toISOString(),
      estimatedDelivery: values.estimatedDelivery ? values.estimatedDelivery.toISOString() : null,
      arriveDate: values.arriveDate ? values.arriveDate.toISOString() : null,
      billVoided: values.billVoided,
      isFreeDrug: values.isFreeDrug,
      assignees: values.assignees,
      unitCost: values.unitCost,
      quantity: values.quantity,
      totalAmount: values.totalAmount,
      lotNumberDispensed: values.lotNumberDispensed,
      lotNumberReceived: values.lotNumberReceived,
      accountNumber: values.accountNumber,
      billVoidedDate: values.billVoidedDate ? values.billVoidedDate.toISOString() : null
    };

    const newCycles = cycles.map((cycle) => {
      let billVoided = cycle.billVoided;
      let administrationDate = cycle.administrationDate;
      let orderDate = cycle.orderDate;
      let arriveDate = cycle.arriveDate;
      let estimatedDelivery = cycle.estimatedDelivery;
      let isFreeDrug = cycle.isFreeDrug;
      let unitCost = cycle.unitCost;
      let totalAmount = cycle.totalAmount;
      let quantity = cycle.quantity;
      let lotNumberDispensed = cycle.lotNumberDispensed;
      let lotNumberReceived = cycle.lotNumberReceived;
      let accountNumber = cycle.accountNumber;
      let billVoidedDate = cycle.billVoidedDate;

      if (cycle.id === displayOrder.id) {
        billVoided = values.billVoided;
        administrationDate = values.administrationDate.toISOString();
        orderDate = values.orderDate.toISOString();
        arriveDate = values.arriveDate ? values.arriveDate.toISOString() : null;
        estimatedDelivery = values.estimatedDelivery ? values.estimatedDelivery.toISOString() : null;
        isFreeDrug = values.isFreeDrug;
        quantity = values.quantity;
        unitCost = values.unitCost || values.unitCost == 0 ? values.unitCost : "";
        totalAmount = values.totalAmount || values.totalAmount == 0 ? values.totalAmount : "";
        lotNumberDispensed = values.lotNumberDispensed;
        lotNumberReceived = values.lotNumberReceived;
        accountNumber = values.accountNumber;
        billVoidedDate = values.billVoidedDate ? values.billVoidedDate.toISOString() : null;
      }

      return {
        id: cycle.id,
        administrationDate,
        orderDate,
        arriveDate: arriveDate || "",
        estimatedDelivery: estimatedDelivery || "",
        billVoided,
        isFreeDrug,
        unitCost,
        quantity,
        lotNumberDispensed,
        lotNumberReceived,
        accountNumber,
        totalAmount,
        billVoidedDate: billVoidedDate || ""
      };
    });
    bulkChangeAct(pharmacyPatient, newCycles);
  };

  const onCancel = () => {
    setEditable(false);
    setEdited(false);
    initialValuesRef.current = null;
    form.resetFields();
  };

  const normalizeValue = (value) => {
    if (value === null || value === undefined || value === "" || value === 0 || value === "0") {
      return null;
    }

    // Convert string numbers to actual numbers for comparison
    if (typeof value === "string" && !isNaN(value) && value.trim() !== "") {
      return Number(value);
    }

    return value;
  };

  const handleFormChange = () => {
    // Only set edited to true if form is in editable mode and fields have actually changed
    if (editable && initialValuesRef.current) {
      const currentValues = form.getFieldsValue();
      const initialValues = initialValuesRef.current;

      const isModified = Object.keys(currentValues).some((key) => {
        const currentValue = currentValues[key];
        const initialValue = initialValues[key];

        if (currentValue && currentValue._isAMomentObject && initialValue && initialValue._isAMomentObject) {
          return !currentValue.isSame(initialValue);
        }

        if (Array.isArray(currentValue) && Array.isArray(initialValue)) {
          return JSON.stringify(currentValue.sort()) !== JSON.stringify(initialValue.sort());
        }

        // Normalize and compare values (treat null, undefined, "" as equivalent)
        const normalizedCurrent = normalizeValue(currentValue);
        const normalizedInitial = normalizeValue(initialValue);

        if (normalizedCurrent === null && normalizedInitial === null) {
          return false;
        }

        return normalizedCurrent !== normalizedInitial;
      });

      setEdited(isModified);
    }
  };

  const { assignees, potentialSaving, isActive, numberOfCycles } = patient;
  const {
    orderIndex,
    administrationDate,
    orderDate,
    billVoided,
    arriveDate,
    estimatedDelivery,
    isFreeDrug,
    status,
    unitCost,
    quantity,
    lotNumberDispensed,
    lotNumberReceived,
    accountNumber,
    totalAmount,
    billVoidedDate
  } = displayOrder;
  const { id: papId } = potentialSaving;

  const updateCalculations = ({ quantity, unitCost, totalAmount }) => {
    form.setFieldsValue({ quantity, unitCost, totalAmount });
  };

  const handleOrderChange = (orderId) => {
    const orderExists = cycles && cycles.find((cycle) => cycle.id === orderId);

    // If order exists, use it; otherwise, fall back to currentOrder
    if (orderExists) {
      dispatch(ACTIONS.setSelectedOrder(orderId));
    } else if (patient.currentOrder && patient.currentOrder.id) {
      dispatch(ACTIONS.setSelectedOrder(patient.currentOrder.id));
    }
  };

  return (
    <div style={{ padding: "0px 16px 0px 0px" }}>
      <Row style={{ display: "flex", justifyContent: "space-between" }}>
        <Space size={16}>
          <Text strong style={{ fontSize: sizes.medium_large }}>
            {t("patient.general.order_info")}
          </Text>
          {cycles && cycles.length > 0 && (
            <OrderSwitchingDropdown
              orders={cycles}
              selectedOrderId={selectedOrderId}
              currentOrderIndex={patient.currentOrder.orderIndex}
              onOrderChange={handleOrderChange}
            />
          )}
          {isOverdue(orderDate, status, isFreeDrug) && (
            <div style={{ display: "flex", alignItems: "center" }}>
              <IsOverdueIcon />
              <Text style={{ paddingLeft: 6, color: "#CA367E" }}>{t("patient.general.is_overdue")}</Text>
            </div>
          )}
        </Space>
        {!editable ? (
          <EditButton
            id="cct-edit-btn"
            disabled={!isActive}
            onClick={() => {
              initialValuesRef.current = form.getFieldsValue();
              setEditable(!editable);
            }}
          />
        ) : null}
      </Row>
      <Row style={{ padding: "16px 0px 16px 0px" }}>
        <PatientOrderProgress patient={patient} order={displayOrder} id="cycle_action_btn_currentcycle" />
      </Row>
      <SingleCycleForm
        form={form}
        name="orderInfo"
        initialValues={{
          isFreeDrug,
          orderDate: moment(orderDate),
          administrationDate: moment(administrationDate),
          arriveDate: arriveDate ? moment(arriveDate) : null,
          estimatedDelivery: estimatedDelivery ? moment(estimatedDelivery) : null,
          billVoided,
          assignees: assignees.map((assignee) => assignee.id),
          quantity,
          lotNumberDispensed,
          lotNumberReceived,
          accountNumber,
          unitCost,
          billVoidedDate: billVoidedDate ? moment(billVoidedDate) : null,
          totalAmount
        }}
        onFinish={(values) => onSubmit(values)}
        onFieldsChange={handleFormChange}
      >
        <StyledRow gutter={[40, 0]}>
          <Col span={6}>
            <OrderTypeFormItem
              defaultValue={isFreeDrug ? OrderType.free.value(t) : OrderType.replacement.value(t)}
              showLabel={true}
              style={{ display: "inline-block" }}
              editable={editable}
              required={true}
              dataTMId="cct-order-type"
            />
          </Col>
          <Col span={6}>
            <AdminDateFormItem
              defaultValue={administrationDate}
              showLabel={true}
              editable={editable}
              required={true}
              style={{ display: "inline-block" }}
              dataTMId="cct-admin-date"
            />
          </Col>
          <Col span={12}>
            <div style={{ paddingTop: 6 }}>
              <Text style={{ color: SEC_FONT_GREY }}>{t("patient.general.treatment_cycles")}</Text>
              <div data-tm-id="cct-treatment-cycles" style={{ paddingTop: 10 }}>
                <Progress
                  style={{ width: "60%" }}
                  percent={((orderIndex + 1) * 100) / numberOfCycles}
                  showInfo={false}
                  trailColor={"#DADADA"}
                  strokeColor={"#2B56A3"}
                />
                <Text style={{ margin: "0 8px" }}>{`${orderIndex + 1}/${numberOfCycles}`}</Text>
              </div>
            </div>
          </Col>
        </StyledRow>
        <StyledRow gutter={[40, 0]}>
          <Col span={6}>
            <OrderDateFormItem
              defaultValue={orderDate}
              showLabel={true}
              editable={editable}
              required={true}
              style={{ display: "inline-block" }}
              dataTMId="cct-order-date"
            />
          </Col>
          <Col span={6}>
            <EstimatedDateFormItem
              defaultValue={estimatedDelivery}
              showLabel={true}
              editable={editable}
              style={{ display: "inline-block" }}
              dataTMId="cct-estimated-delivery"
            />
          </Col>
          <Col span={6}>
            <ArrivedDateFormItem
              defaultValue={arriveDate}
              showLabel={true}
              editable={editable}
              style={{ display: "inline-block" }}
              id="orderInfo_receivedDate"
              dataTMId="cct-received-date"
            />
          </Col>
          <Col span={6}>
            <LotNumberFormItem
              name="lotNumberReceived"
              dataTMId="cct-lot-number-received"
              defaultValue={lotNumberReceived}
              placeholder={t("form.lot_number_received.placeholder")}
              editable={editable}
              showLabel={true}
              label={t("form.lot_number_received.label")}
              labelStyle={{ color: SEC_FONT_GREY }}
              minNumberOfDigits={0}
              maxNumberOfDigits={LOT_NUMBER_MAX}
            />
          </Col>
        </StyledRow>
        <StyledRow gutter={[40, 0]}>
          <Col span={6}>
            <NumericalInput
              name="quantity"
              dataTMId="cct-quantity"
              defaultValue={quantity}
              placeholder={t("form.quantity.placeholder")}
              editable={editable}
              strong={false}
              showLabel={true}
              required={true}
              label={t("form.quantity.label")}
              labelStyle={{ color: SEC_FONT_GREY }}
              rules={[{ required: true }]}
            />
          </Col>
          <Col span={6}>
            <NumericalInput
              name="unitCost"
              dataTMId="cct-unitCost"
              placeholder={t("form.unitCost.placeholder")}
              defaultValue={unitCost}
              prefix="$"
              editable={editable}
              showLabel={true}
              label={t("form.unitCost.label")}
              labelStyle={{ color: SEC_FONT_GREY }}
            />
          </Col>
          <Col span={6}>
            <NumericalInput
              name="totalAmount"
              dataTMId="cct-totalAmount"
              placeholder={t("form.totalAmount.placeholder")}
              defaultValue={totalAmount}
              prefix="$"
              editable={editable}
              showLabel={true}
              label={t("form.totalAmount.label")}
              labelStyle={{ color: SEC_FONT_GREY }}
            />
          </Col>
          {editable && (
            <Col style={{ display: "flex", alignItems: "center", paddingLeft: "0px", marginLeft: "-10px" }}>
              <DrugPriceCalculator papId={papId} onConfirm={updateCalculations} />
            </Col>
          )}
        </StyledRow>
        <StyledRow gutter={[40, 0]}>
          <Col span={6}>
            <LotNumberFormItem
              name="accountNumber"
              dataTMId="cct-account-number"
              defaultValue={accountNumber}
              placeholder={t("form.account_number.placeholder")}
              editable={editable}
              showLabel={true}
              label={t("form.account_number.label")}
              labelStyle={{ color: SEC_FONT_GREY }}
              minNumberOfDigits={0}
              maxNumberOfDigits={LOT_NUMBER_MAX}
            />
          </Col>
          <Col span={6}>
            <BillVoidedDateFormItem
              form={form}
              style={{ display: "inline-block" }}
              editable={editable}
              showLabel={true}
              dataTMId="cct-bill-voided"
              defaultDateValue={billVoidedDate}
            />
          </Col>
          <Col span={6}>
            <LotNumberFormItem
              name="lotNumberDispensed"
              dataTMId="cct-lot-number-dispensed"
              defaultValue={lotNumberDispensed}
              placeholder={t("form.lot_number_dispensed.placeholder")}
              editable={editable}
              showLabel={true}
              label={t("form.lot_number_dispensed.label")}
              labelStyle={{ color: SEC_FONT_GREY }}
              minNumberOfDigits={0}
              maxNumberOfDigits={LOT_NUMBER_MAX}
            />
          </Col>
        </StyledRow>

        <StyledRow gutter={[40, 0]}>
          <Col span={24}>
            <Form.Item
              colon={false}
              label={<Text style={{ color: SEC_FONT_GREY }}>{t("patient.general.cycle_notes")}</Text>}
              style={{ display: "inline-block", paddingRight: 16 }}
            >
              {!currCycleNotes.length && <Text data-tm-id="cct-cycle-notes">{"-"}</Text>}
              {currCycleNotes.length > 0 && (
                <Table
                  data-tm-id="cct-cycle-notes"
                  bordered={false}
                  showHeader={false}
                  columns={[
                    {
                      dataIndex: "note",
                      align: "left",
                      render(text) {
                        return {
                          props: {
                            style: { padding: 0, border: 0 }
                          },
                          children: <Text id="current_cycle_notes">{`- ${text}`}</Text>
                        };
                      }
                    }
                  ]}
                  dataSource={currCycleNotes.map((patientNote, index) => ({
                    ...patientNote,
                    key: index
                  }))}
                  scroll={{ y: 70 }}
                  pagination={false}
                />
              )}
            </Form.Item>
          </Col>
        </StyledRow>
        {editable ? (
          <Form.Item>
            <Space>
              <Button id="cct-edit-order-cancel" onClick={onCancel}>
                {t("cancel")}
              </Button>
              <Button loading={isFetching} id="cct-edit-order-submit" type="primary" htmlType="submit">
                {t("save")}
              </Button>
            </Space>
          </Form.Item>
        ) : null}
      </SingleCycleForm>
    </div>
  );
};

const mapStateToProps = createStructuredSelector({
  isFetching: selectIsFetching([ApiNames.BulkChange]),
  cycles: selectPatientCycles,
  patientNotes: selectPatientNotes
});

const mapDispatchToProps = (dispatch) => ({
  bulkChangeAct: (pharmacyPatient, orders) => dispatch(ACTIONS.bulkChangeAction(pharmacyPatient, orders))
});

const PatientOrderInfo = connect(mapStateToProps, mapDispatchToProps)(withTranslation()(PatientOrderInfoComponent));

export { PatientOrderInfo };
